// Import check for credit-sale lines pulled from Tally. It looks at every line and flags what
// can't be trusted, so a mistake is caught before an invoice goes out:
//
//   error   -> blocks printing / saving until fixed (the invoice would be wrong)
//   warning -> needs a person to confirm ("I have checked") - the importer guessed
//
// Each rule exists because of a real miss: a line with no Item added to the total but
// printed nowhere; cash vouchers booked to "CASH SALES" with no "(CASH)" in the narration.
export const FUEL_PRODUCTS = ['Petrol', 'Diesel', 'Xtragreen'];

const num = (v) => parseFloat(v) || 0;
const rupees = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
const paise = (n) => Math.round(n * 100) / 100;

const LARGE_CASH = 5000; // a driver cash advance above this is unusual enough to double-check
const RATE_TOLERANCE = 1; // quantity is rounded to 2 decimals, so qty x rate can be a few paise off the amount

const isRealIsoDate = (d) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d || '')) return false;
  const dt = new Date(`${d}T00:00:00Z`);
  return !Number.isNaN(dt.getTime()) && dt.toISOString().slice(0, 10) === d;
};
const voucherKey = (v) => String(v || '').replace(/\s+/g, '').toUpperCase();

// options.separateCash - the invoice is printed with cash in its own table, so a fuel row's own
// Cash column is not shown anywhere
export function flagEntries(entries, options = {}) {
  const byId = {};
  const add = (e, level, code, message) => {
    (byId[e.id] = byId[e.id] || []).push({ level, code, message });
  };

  const voucherCount = {};
  entries.forEach((e) => {
    const k = voucherKey(e.voucher_no);
    if (k) voucherCount[k] = (voucherCount[k] || 0) + 1;
  });

  // Same day, vehicle, product and amount under different voucher numbers: either two real
  // identical fills or one entered twice - only a person can tell
  const lookalikeKey = (e) => [e.business_date, String(e.vehicle_no || '').toUpperCase(), e.item_name, paise(num(e.amount) + num(e.cash_amount))].join('|');
  const lookalikes = {};
  entries.forEach((e) => {
    if (!e.vehicle_no || !e.business_date || !e.item_name) return;
    (lookalikes[lookalikeKey(e)] = lookalikes[lookalikeKey(e)] || []).push(e);
  });

  entries.forEach((e) => {
    const fuel = FUEL_PRODUCTS.includes(e.item_name);
    const cash = e.item_name === 'CASH';
    const amount = num(e.amount);
    const cashAmount = num(e.cash_amount);
    const total = amount + cashAmount;

    if (!fuel && !cash) {
      if (total !== 0) {
        add(e, 'error', 'NO_ITEM', `No valid Item - this ${rupees(total)} line would not print or be counted. Choose Petrol, Diesel, Xtragreen or CASH (or delete the line).`);
      }
      return;
    }

    if (amount < 0 || cashAmount < 0 || num(e.quantity) < 0 || num(e.rate) < 0) {
      add(e, 'error', 'NEGATIVE', 'A negative figure on this line - amounts, quantity and rate must be positive.');
    } else if (total <= 0) {
      add(e, 'error', 'NO_AMOUNT', 'This line has no amount (zero).');
    }
    if (!isRealIsoDate(e.business_date)) {
      add(e, 'error', 'NO_DATE', 'No valid date - it would print as 01-01-1970. Set the date.');
    }
    if (voucherCount[voucherKey(e.voucher_no)] > 1) {
      add(e, 'error', 'DUPLICATE', `Voucher ${e.voucher_no} appears more than once - it would be billed twice. Delete the extra line.`);
    }

    if (cash) {
      if (amount !== 0) {
        add(e, 'error', 'CASH_IN_AMOUNT', `This is a CASH line but ${rupees(amount)} is in the Amount column - cash belongs in the Cash column, otherwise it is added to the total but printed nowhere.`);
      }
      if (e.cash_source === 'ledger') {
        add(e, 'warning', 'CASH_BY_LEDGER', 'Treated as cash only because its sales ledger is "CASH SALES" - the narration has no "(CASH)". Confirm it is cash given to the driver.');
      } else if (e.cash_source === 'narration-loose') {
        add(e, 'warning', 'CASH_LOOSE_TAG', 'The narration mentions cash but not as "(CASH)" - it was treated as cash given to the driver. Confirm that is right.');
      }
      if (cashAmount > LARGE_CASH) {
        add(e, 'warning', 'LARGE_CASH', `Large cash amount (${rupees(e.cash_amount)}) - confirm it is cash given, not a fuel fill.`);
      }
      if (paise(cashAmount) % 100 !== 0) {
        add(e, 'warning', 'ODD_CASH', `Cash amount ${rupees(e.cash_amount)} is not a round figure - confirm it is right.`);
      }
    }

    if (fuel) {
      if (options.separateCash && cashAmount !== 0) {
        add(e, 'error', 'FUEL_CASH_HIDDEN', `${rupees(cashAmount)} is in this fuel row's Cash column, but the "Separate cash table" format only prints CASH lines - it would be counted in the total and not shown. Make it its own CASH line.`);
      }
      if (num(e.quantity) <= 0 || !num(e.rate)) {
        add(e, 'warning', 'NO_QTY_OR_RATE', 'No fuel rate was found for this date, so the quantity could not be worked out. Enter the quantity and rate.');
      } else if (Math.abs(num(e.quantity) * num(e.rate) - amount) > RATE_TOLERANCE) {
        add(e, 'warning', 'QTY_RATE_MISMATCH', `Quantity x rate comes to ${rupees(paise(num(e.quantity) * num(e.rate)))} but the amount is ${rupees(amount)}.`);
      }
      if (Math.abs(amount - Math.round(amount)) > 0.001) {
        add(e, 'warning', 'FRACTIONAL_AMOUNT', `Amount ${rupees(amount)} has paise - the invoice prints whole rupees per row, so the rows will not add up to the total.`);
      }
    }

    if (e.extra_ledgers && e.extra_ledgers.length > 0) {
      add(e, 'warning', 'MULTI_LEDGER', `This voucher lists more than one ledger (${[e.ledger_name, ...e.extra_ledgers].filter(Boolean).join(' + ')}) but only one line was made from it. Check the item and amount.`);
    }

    const twins = e.vehicle_no && e.business_date ? lookalikes[lookalikeKey(e)] : null;
    if (twins && twins.length > 1 && new Set(twins.map(x => voucherKey(x.voucher_no))).size > 1) {
      add(e, 'warning', 'LOOKALIKE', 'Another line has the same date, vehicle, item and amount under a different voucher number - confirm both are real.');
    }

    if (!e.vehicle_no) add(e, 'warning', 'NO_VEHICLE', 'No vehicle number could be read from the narration.');
    if (!e.voucher_no) add(e, 'warning', 'NO_VOUCHER', 'No voucher number.');
  });

  const label = (e) => ({
    id: e.id,
    date: e.business_date,
    voucher_no: e.voucher_no || '-',
    vehicle_no: e.vehicle_no || '-',
    amount: num(e.amount) + num(e.cash_amount),
    item: e.item_name || '-'
  });

  const errors = [];
  const warnings = [];
  entries.forEach((e) => {
    (byId[e.id] || []).forEach((f) => {
      (f.level === 'error' ? errors : warnings).push({ ...label(e), code: f.code, message: f.message });
    });
  });

  return { byId, errors, warnings };
}

// Checks on the invoice as a whole (not one line): things that make the printed figures
// wrong even when every line is fine. Items have no id/voucher - they carry scope 'invoice'.
//   fuelQuantities - { Diesel: litres, ... } of what will actually print
//   totals         - { discountAmount, grandTotal }
export function checkInvoice({ fuelQuantities = {}, discountProduct, discountPerLitre, totals = {}, receipts = [], balanceDue = 0 }) {
  const items = [];
  const add = (level, code, message) => items.push({ scope: 'invoice', level, code, message });

  const rate = num(discountPerLitre);
  if (rate > 0 && num(fuelQuantities[discountProduct]) <= 0) {
    add('warning', 'DISCOUNT_NO_LITRES', `A discount of ₹${rate}/L is set to apply to ${discountProduct}, but this invoice has no ${discountProduct} - the discount works out to ₹0.00. Change "Discount applies to".`);
  }
  if (rate < 0 || num(totals.discountAmount) < 0) {
    add('error', 'NEGATIVE_DISCOUNT', 'The discount is negative.');
  }
  if (num(totals.grandTotal) < 0) {
    add('error', 'NEGATIVE_TOTAL', 'The discount / TFR is larger than the invoice total, so G.TOTAL is negative.');
  }
  receipts.forEach((r) => {
    if (!(num(r.amount) > 0)) {
      add('warning', 'RECEIPT_NO_AMOUNT', `A receipt${r.voucher_no ? ` (#${r.voucher_no})` : ''} has no amount - delete it or enter the amount.`);
    }
  });
  if (receipts.length > 0 && num(balanceDue) < 0) {
    add('warning', 'OVERPAID', 'The receipts are more than the invoice total, so the balance due is negative. Check the receipts below.');
  }
  return items;
}

// The per-line markers once a person has confirmed the warnings: real problems stay, warnings go
export const withoutWarnings = (byId) => {
  const out = {};
  Object.keys(byId).forEach((id) => {
    const errors = byId[id].filter((f) => f.level === 'error');
    if (errors.length) out[id] = errors;
  });
  return out;
};

// Cash given and fuel are billed in different tables, so a line the check can't place
// is the one thing that must never be silently dropped from the totals.
export const isPrintableItem = (item) => FUEL_PRODUCTS.includes(item) || item === 'CASH';
